import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Power, KeyRound, X } from "lucide-react";
import api, { errMsg } from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";

const PERM_GROUPS = [
  { title: "General", perms: [["dashboard", "Dashboard"], ["oa_history", "OA History"], ["reports", "Reports"]] },
  { title: "Order Acceptance", perms: [["create_oa", "Create OA"], ["view_oa", "View OA"], ["edit_oa", "Edit OA"], ["delete_oa", "Delete OA"], ["duplicate_oa", "Duplicate OA"], ["print_oa", "Print OA"], ["download_pdf", "Download PDF"]] },
  { title: "OA Viewing Access (Time Window)", perms: [["view_oa_all", "View All OA"], ["view_oa_15d", "View Last 15 Days OA"], ["view_oa_30d", "View Last 30 Days OA"], ["view_oa_3m", "View Past 3 Months OA"], ["view_oa_6m", "View Past 6 Months OA"], ["view_oa_1y", "View Past 1 Year OA"]] },
  { title: "Purchase Orders (PO)", perms: [["upload_po", "Upload PO"], ["view_all_po", "View All PO"], ["view_po", "View PO"], ["download_po", "Download PO"], ["delete_po", "Delete PO"]] },
  { title: "Customers", perms: [["add_customer", "Add Customer"], ["edit_customer", "Edit Customer"], ["delete_customer", "Delete Customer"]] },
  { title: "Products", perms: [["add_product", "Add Product"], ["edit_product", "Edit Product"], ["delete_product", "Delete Product"]] },
  { title: "Annexure", perms: [["upload_annexure", "Upload Annexure"], ["view_annexure", "View Annexure"], ["download_annexure", "Download Annexure"], ["delete_annexure", "Delete Annexure"]] },
  { title: "Quotation", perms: [["upload_quotation", "Upload Quotation"], ["view_quotation", "View Quotation"], ["download_quotation", "Download Quotation"], ["print_quotation", "Print Quotation"], ["delete_quotation", "Delete Quotation"]] },
  { title: "Bill", perms: [["upload_bill", "Upload Bill"], ["view_bill", "View Bill"], ["download_bill", "Download Bill"], ["delete_bill", "Delete Bill"]] },
  { title: "Workflow & Data", perms: [["change_oa_status", "Change OA Status"], ["import_data", "Import Data"], ["export_data", "Export Data"]] },
  { title: "Master Data", perms: [["view_master", "Search / View Masters"], ["add_master", "Add Masters"], ["edit_master", "Edit Masters"], ["delete_master", "Delete Masters"], ["view_aadhaar", "View Aadhaar Documents (restricted)"]] },
  { title: "Office Work", perms: [["view_office_work", "View"], ["add_office_work", "Add"], ["edit_office_work", "Edit"], ["delete_office_work", "Delete"]] },
  { title: "Invoice", perms: [["view_invoice", "View"], ["add_invoice", "Add"], ["edit_invoice", "Edit"], ["delete_invoice", "Delete"]] },
  { title: "Item / Product Tracking", perms: [["view_item", "View"], ["add_item", "Add"], ["edit_item", "Edit"], ["delete_item", "Delete"]] },
  { title: "Dispatch", perms: [["view_dispatch", "View"], ["add_dispatch", "Add"], ["edit_dispatch", "Edit"], ["delete_dispatch", "Delete"]] },
  { title: "Transporter", perms: [["view_transporter", "View"], ["add_transporter", "Add"], ["edit_transporter", "Edit"], ["delete_transporter", "Delete"]] },
  { title: "Installer", perms: [["view_installer", "View"], ["add_installer", "Add"], ["edit_installer", "Edit"], ["delete_installer", "Delete"]] },
  { title: "Work Order", perms: [["view_workorder", "View"], ["add_workorder", "Add"], ["edit_workorder", "Edit"], ["delete_workorder", "Delete"]] },
];
const ALL_PERM_KEYS = PERM_GROUPS.flatMap((g) => g.perms.map((p) => p[0]));
const PERM_LABEL = Object.fromEntries(PERM_GROUPS.flatMap((g) => g.perms));
const empty = { name: "", email: "", loginId: "", password: "", mobile: "", permissions: ["dashboard", "create_oa", "view_oa", "oa_history", "print_oa", "download_pdf", "add_customer", "upload_po", "view_all_po", "view_po", "download_po", "view_oa_all", "view_master", "add_master", "view_workorder", "add_workorder"] };

export default function Employees() {
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(empty);
  const [pwdModal, setPwdModal] = useState(null);
  const [newPwd, setNewPwd] = useState("");

  const load = async () => { const { data } = await api.get("/employees"); setRows(data.filter((u) => u.role === "employee")); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setModal("new"); };
  const openEdit = (e) => { setForm({ ...e, password: "" }); setModal(e.id); };
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const togglePerm = (k) => setForm((p) => ({ ...p, permissions: p.permissions.includes(k) ? p.permissions.filter((x) => x !== k) : [...p.permissions, k] }));

  const save = async () => {
    if (!form.name || !form.email || !form.loginId) return toast.error("Name, email and login ID required.");
    try {
      if (modal === "new") {
        if (!form.password) return toast.error("Password required for new employee.");
        await api.post("/employees", form);
      } else {
        await api.put(`/employees/${modal}`, { name: form.name, email: form.email, loginId: form.loginId, mobile: form.mobile, permissions: form.permissions });
      }
      toast.success("Saved"); setModal(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (e) => {
    try { await api.patch(`/employees/${e.id}/status`, null, { params: { status: e.status === "active" ? "inactive" : "active" } }); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const resetPwd = async () => {
    try { await api.post(`/employees/${pwdModal}/reset-password`, { newPassword: newPwd }); toast.success("Password reset"); setPwdModal(null); setNewPwd(""); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Employee Management" subtitle={`${rows.length} employee(s)`}
        actions={<button className="btn-accent" onClick={openNew} data-testid="add-employee-btn"><Plus className="h-4 w-4" /> Add Employee</button>} />
      <div className="p-6 lg:p-8">
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200"><tr>
              <th className="th">Name</th><th className="th">Login ID</th><th className="th">Email</th><th className="th">Mobile</th><th className="th">Permissions</th><th className="th">Status</th><th className="th">Actions</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && <tr><td className="td text-slate-400" colSpan={7}>No employees yet.</td></tr>}
              {rows.map((e) => (
                <tr key={e.id} className="hover:bg-slate-50" data-testid={`employee-row-${e.id}`}>
                  <td className="td font-semibold text-slate-900">{e.name}</td>
                  <td className="td font-mono text-xs">{e.loginId}</td>
                  <td className="td">{e.email}</td>
                  <td className="td">{e.mobile || "—"}</td>
                  <td className="td text-xs">{(e.permissions || []).length ? `${e.permissions.length} feature(s)` : "OA only"}</td>
                  <td className="td"><StatusBadge status={e.status} /></td>
                  <td className="td"><div className="flex gap-1">
                    <button className="p-1.5 rounded hover:bg-slate-100" onClick={() => openEdit(e)} data-testid={`edit-employee-${e.id}`}><Pencil className="h-4 w-4 text-slate-600" /></button>
                    <button className="p-1.5 rounded hover:bg-slate-100" onClick={() => { setPwdModal(e.id); setNewPwd(""); }} data-testid={`reset-pwd-${e.id}`}><KeyRound className="h-4 w-4 text-amber-600" /></button>
                    <button className="p-1.5 rounded hover:bg-slate-100" onClick={() => toggle(e)} data-testid={`toggle-employee-${e.id}`}><Power className={`h-4 w-4 ${e.status === "active" ? "text-green-600" : "text-slate-400"}`} /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="card w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="employee-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-head text-xl font-bold">{modal === "new" ? "Add Employee" : "Edit Employee"}</h3>
              <button onClick={() => setModal(null)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="label">Name *</label><input className="input" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="emp-name" /></div>
              <div><label className="label">Login ID *</label><input className="input" value={form.loginId} onChange={(e) => set("loginId", e.target.value)} data-testid="emp-loginid" /></div>
              <div><label className="label">Email *</label><input className="input" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="emp-email" /></div>
              <div><label className="label">Mobile</label><input className="input" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} /></div>
              {modal === "new" && <div className="md:col-span-2"><label className="label">Password *</label><input type="password" className="input" value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="emp-password" /></div>}
            </div>
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <label className="label mb-0">Access Management — Feature Permissions</label>
                <div className="flex gap-2">
                  <button type="button" className="btn-ghost btn-sm" onClick={() => setForm((p) => ({ ...p, permissions: [...ALL_PERM_KEYS] }))} data-testid="perm-select-all">Select all</button>
                  <button type="button" className="btn-ghost btn-sm" onClick={() => setForm((p) => ({ ...p, permissions: [] }))} data-testid="perm-clear-all">Clear</button>
                </div>
              </div>
              <div className="space-y-4">
                {PERM_GROUPS.map((g) => (
                  <div key={g.title}>
                    <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">{g.title}</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {g.perms.map(([key, label]) => (
                        <label key={key} className={`flex items-center gap-2 text-sm cursor-pointer border rounded-lg px-3 py-2 ${form.permissions.includes(key) ? "border-amber-500 bg-amber-50" : "border-slate-200"}`}>
                          <input type="checkbox" className="h-4 w-4 accent-amber-600" checked={form.permissions.includes(key)} onChange={() => togglePerm(key)} data-testid={`perm-${key}`} /> {label}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button className="btn-outline" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn-primary" onClick={save} data-testid="save-employee-btn">Save</button>
            </div>
          </div>
        </div>
      )}

      {pwdModal && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={() => setPwdModal(null)}>
          <div className="card w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-head text-lg font-bold mb-4">Reset Password</h3>
            <input type="password" className="input mb-4" placeholder="New password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} data-testid="reset-pwd-input" />
            <div className="flex justify-end gap-2">
              <button className="btn-outline" onClick={() => setPwdModal(null)}>Cancel</button>
              <button className="btn-primary" onClick={resetPwd} data-testid="confirm-reset-pwd">Reset</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
